// Knowledge-base shapes are generated from OrbitalX.Dto — see api-generated.d.ts.

import type {
  BrandAssetBackground,
  BrandAssetType,
  BrandTokenProvenanceViewDto as GeneratedBrandTokenProvenanceViewDto,
  BrandTokenRole,
  BrandTokenSetViewDto as GeneratedBrandTokenSetViewDto,
  BrandTokenSource,
  DesignSystemImportChangeDto as GeneratedDesignSystemImportChangeDto,
  DesignSystemImportChangeKind,
  DesignSystemImportLogoChoiceDto as GeneratedDesignSystemImportLogoChoiceDto,
  DesignSystemImportLogoDto as GeneratedDesignSystemImportLogoDto,
  DesignSystemImportPreviewDto as GeneratedDesignSystemImportPreviewDto,
  DesignSystemImportRoleChoiceDto as GeneratedDesignSystemImportRoleChoiceDto,
  DesignSystemImportRoleDto as GeneratedDesignSystemImportRoleDto,
  KnowledgeBaseCategoryConfigDto,
  KnowledgeBaseCategoryStatusDto as GeneratedKnowledgeBaseCategoryStatusDto,
  KnowledgeBaseEntryUpsertDto as GeneratedKnowledgeBaseEntryUpsertDto,
  KnowledgeBaseEntryViewDto as GeneratedKnowledgeBaseEntryViewDto,
  KnowledgeBaseFileUploadDto as GeneratedKnowledgeBaseFileUploadDto,
  KnowledgeBaseFileViewDto as GeneratedKnowledgeBaseFileViewDto,
} from './api-generated'

export type {
  BrandAssetBackground,
  BrandAssetType,
  BrandColorRoleViewDto,
  BrandColorTokenViewDto,
  BrandRolesViewDto,
  BrandTokenPatchDto,
  BrandTokenConfirmationDto,
  BrandTokenFamilyKind,
  BrandTokenRole,
  BrandTokenRolesPatchDto,
  BrandTokenSource,
  BrandTokenViewDto,
  BrandTypeFamilyPatchDto,
  BrandTypeGroupViewDto,
  BrandTypeRoleViewDto,
  BrandTypeStylePatchDto,
  BrandTypeStyleViewDto,
  BrandTypeViewDto,
  BrandValueRoleViewDto,
  DesignSystemImportChangeKind,
  DesignSystemImportDroppedDto,
  DesignSystemImportRoleOptionDto,
  KnowledgeBaseAiPopulateDto,
  KnowledgeBaseCategoryConfigDto,
  KnowledgeBaseSummaryViewDto,
} from './api-generated'

// ============================================================================
// ENUMS
// ============================================================================

export enum KnowledgeBaseCategory {
  Snapshot = 'Snapshot',
  IdealCustomerProfile = 'Ideal Customer Profile',
  Content = 'Content',
  ToneOfVoice = 'Tone of Voice',
  CoreNarrativeAndPillars = 'Core Narrative and Pillars',
  BrandGuidelines = 'Brand Guidelines',
  VisualNarrative = 'Visual Narrative',
  PainPoints = 'Pain Points',
  Offers = 'Offers',
  Keywords = 'Keywords',
  MissionVisionValues = 'Mission, Vision, and Values',
  Objections = 'Objections',
  CaseStudiesAndTestimonials = 'Case Studies and Testimonials',
  DefaultMarketingLanguage = 'Default Marketing Language',
  Faqs = 'FAQs',
  Compliance = 'Compliance',
  CompanyAddress = 'Company Address',
  PrivacyPolicy = 'Privacy Policy',
}

export enum KnowledgeBaseLevel {
  Company = 'Company',
  Workspace = 'Workspace',
  Campaign = 'Campaign',
}

// Use a const object instead of a TypeScript enum to avoid circular-import
// timing issues that cause the enum to resolve as `undefined` at runtime
// in Vite/ESM builds (observed as: Cannot read properties of undefined (reading 'USEnglish')).
//
// ⚑ These are the enum IDENTIFIERS, not the [Description] values, and that is correct here even
// though the wire convention is normally the description. It travels as DropdownValue, and
// KnowledgeBaseValidator.ValidMarketingLanguages accepts exactly "USEnglish" / "UKEnglish" —
// the AI populate prompt emits the same. Do not "fix" this to "US English".
export const MarketingLanguage = {
  USEnglish: 'USEnglish',
  UKEnglish: 'UKEnglish',
} as const
export type MarketingLanguage = typeof MarketingLanguage[keyof typeof MarketingLanguage]

// ============================================================================
// ASSET TYPES (for typed file uploads on Brand Guidelines)
// ============================================================================

// The five slots `BrandAssetType` names on the wire. Brand Guidelines' page shows all five (ORB-904).
export type KnowledgeBaseAssetType = BrandAssetType

// ============================================================================
// FILE DTOs
// ============================================================================

export interface KnowledgeBaseFileViewDto extends Omit<GeneratedKnowledgeBaseFileViewDto, 'AssetType' | 'Background'> {
  AssetType?: KnowledgeBaseAssetType | null
  // Null works on either background (ORB-968).
  Background?: BrandAssetBackground | null
}

export interface KnowledgeBaseFileUploadDto extends Omit<GeneratedKnowledgeBaseFileUploadDto, 'AssetType' | 'Background'> {
  AssetType?: KnowledgeBaseAssetType | null
  Background?: BrandAssetBackground | null
}

// ============================================================================
// DESIGN-SYSTEM IMPORT (ORB-1028)
// Generated from OrbitalX.Dto. The fields an enum's description fills are strings on the wire, narrowed here.
// ============================================================================

export interface DesignSystemImportChangeDto extends Omit<GeneratedDesignSystemImportChangeDto, 'Kind'> {
  Kind: DesignSystemImportChangeKind
}

export interface DesignSystemImportLogoDto
  extends Omit<GeneratedDesignSystemImportLogoDto, 'SuggestedAssetType' | 'SuggestedBackground'> {
  SuggestedAssetType?: KnowledgeBaseAssetType | null
  SuggestedBackground?: BrandAssetBackground | null
}

export interface DesignSystemImportRoleDto extends Omit<GeneratedDesignSystemImportRoleDto, 'Role'> {
  Role: BrandTokenRole
}

export interface DesignSystemImportPreviewDto
  extends Omit<GeneratedDesignSystemImportPreviewDto, 'Incoming' | 'Current' | 'Changes' | 'Logos' | 'Roles'> {
  Incoming: BrandTokenSetViewDto
  Current?: BrandTokenSetViewDto | null
  Changes: DesignSystemImportChangeDto[]
  Logos: DesignSystemImportLogoDto[]
  Roles: DesignSystemImportRoleDto[]
}

/** A logo found in the design system: null AssetType leaves it out. */
export interface DesignSystemImportLogoChoice
  extends Omit<GeneratedDesignSystemImportLogoChoiceDto, 'AssetType' | 'Background'> {
  AssetType: KnowledgeBaseAssetType | null
  Background: BrandAssetBackground | null
}

/** The option a person chose for a role the design system's own names do not settle. */
export interface DesignSystemImportRoleChoice extends Omit<GeneratedDesignSystemImportRoleChoiceDto, 'Role'> {
  Role: BrandTokenRole
}

// ============================================================================
// BRAND TOKENS (Brand Guidelines only; written by its own patches, its confirmation and Populate, never by an upsert)
// ============================================================================

export interface BrandTokenProvenanceViewDto extends Omit<GeneratedBrandTokenProvenanceViewDto, 'Source'> {
  Source: BrandTokenSource
}

export interface BrandTokenSetViewDto extends Omit<GeneratedBrandTokenSetViewDto, 'Provenance'> {
  Provenance: BrandTokenProvenanceViewDto
}

// ============================================================================
// ENTRY VIEW DTO (response from GET single category)
// ============================================================================

export interface KnowledgeBaseEntryViewDto extends Omit<GeneratedKnowledgeBaseEntryViewDto, 'Files' | 'BrandTokens'> {
  Files: KnowledgeBaseFileViewDto[]
  BrandTokens?: BrandTokenSetViewDto | null
}

// ============================================================================
// SUMMARY DTOs (response from GET all categories)
// ============================================================================

export type KnowledgeBaseCategoryStatusDto = GeneratedKnowledgeBaseCategoryStatusDto

/**
 * A category whose metadata the server could resolve. `Config` is genuinely nullable on the wire —
 * KnowledgeBaseMapper emits null for a category with no entry in KnowledgeBaseCategoryMetadata — and
 * a row without it cannot be rendered at all, so useKnowledgeBase drops those rather than making
 * every read site guard a value that is either wholly present or wholly absent.
 */
export type KnowledgeBaseCategoryStatusWithConfigDto =
  KnowledgeBaseCategoryStatusDto & { Config: KnowledgeBaseCategoryConfigDto }

/**
 * The upsert body. `IsAiGenerated` and `IsUserReviewed` are non-nullable `bool` in C# and so are
 * required in the generated shape — correct for a response, wrong for a request, where an absent
 * key deserializes to false. The editor has never sent them.
 */
export type KnowledgeBaseEntryUpsertDto =
  Omit<GeneratedKnowledgeBaseEntryUpsertDto, 'IsAiGenerated' | 'IsUserReviewed'>
  & Partial<Pick<GeneratedKnowledgeBaseEntryUpsertDto, 'IsAiGenerated' | 'IsUserReviewed'>>
